import { ampli } from '@/ampli';
import Decimal from 'decimal.js-light';
import React, { useState } from 'react';

import { LiquidityScoreModal } from '@/components/Modal/LiquidityScoreModal';

import { useFiatDisplay } from '@/hooks/useFiatDisplay';
import { useLiquidityScore } from '@/hooks/useLiquidityScore';

import { fontStyles } from '@/styles/typography';

import { LiquidityProviderAvatar } from '@/ui/Avatars/LiquidityProviderAvatar';
import { UnknownBadgeIcon } from '@/ui/Icons/UnknownBadgeIcon';

import { TokenResult } from '@/utils/0x/token-registry.types';
import { CoingeckoTopPoolsByTokenAddressResponse } from '@/utils/coingecko/types';
import { getLiquiditySourceLabel } from '@/utils/liquidity-sources';
import { abbreviateNumber } from '@/utils/numbers';

import { ScoreSectionLoader } from '../ScoreSectionLoader';
import { getVisibleLiquidityScore } from '../TokenLiquidityScore';
import { getScoreIcon } from '../TokenLiquidityScore/getScoreIcon';
import {
  Level0ArcIcon,
  Level10ArcIcon,
  Level1ArcIcon,
  Level2ArcIcon,
  Level3ArcIcon,
  Level4ArcIcon,
  Level5ArcIcon,
  Level6ArcIcon,
  Level7ArcIcon,
  Level8ArcIcon,
  Level9ArcIcon,
  UnknownArcIcon,
} from './ArcIcons';

interface TokenProfileLiquidityScoreProps {
  activeToken: TokenResult | undefined;
  topPools: CoingeckoTopPoolsByTokenAddressResponse | null | undefined;
}

type TopPool = CoingeckoTopPoolsByTokenAddressResponse['data'][number];

const enum LIQUIDITY_LEVEL {
  HIGH = 'High',
  MEDIUM = 'Medium',
  LOW = 'Low',
  UNKNOWN = 'Unknown',
}

export const TokenProfileLiquidityScore = ({
  activeToken,
  topPools,
}: TokenProfileLiquidityScoreProps) => {
  const { currencySymbol, convertFromUsd } = useFiatDisplay();
  const [isOpen, setIsOpen] = useState(false);
  const { data: liquidityData, isLoading } = useLiquidityScore(
    activeToken ? [{ address: activeToken.address, chainId: activeToken.chainId }] : undefined,
  );
  const score = liquidityData?.data?.[0]?.score;
  const blockedTokenListsCount = activeToken?.tokenListsBlock?.length;
  const liquidityScore = getVisibleLiquidityScore(score, blockedTokenListsCount);
  const icon = getScoreIcon(liquidityScore);

  if (isLoading || !activeToken) {
    return (
      <div className="flex flex-col scroll-mt-[62px]">
        <ScoreSectionLoader />
      </div>
    );
  }

  const liquidityLevel = (score?: number): LIQUIDITY_LEVEL => {
    if (score === undefined || score === null) return LIQUIDITY_LEVEL.UNKNOWN;
    if (score >= 6) return LIQUIDITY_LEVEL.HIGH;
    if (score >= 3) return LIQUIDITY_LEVEL.MEDIUM;
    return LIQUIDITY_LEVEL.LOW;
  };

  const currentLiquidityLevel = liquidityLevel(liquidityScore);

  const LiquidityArcIcon = (score?: number): React.ReactElement => {
    const arcIconClass = 'text-token-alphalight-300';
    switch (score) {
      case 10:
        return <Level10ArcIcon className={arcIconClass} />;
      case 9:
        return <Level9ArcIcon className={arcIconClass} />;
      case 8:
        return <Level8ArcIcon className={arcIconClass} />;
      case 7:
        return <Level7ArcIcon className={arcIconClass} />;
      case 6:
        return <Level6ArcIcon className={arcIconClass} />;
      case 5:
        return <Level5ArcIcon className={arcIconClass} />;
      case 4:
        return <Level4ArcIcon className={arcIconClass} />;
      case 3:
        return <Level3ArcIcon className={arcIconClass} />;
      case 2:
        return <Level2ArcIcon className={arcIconClass} />;
      case 1:
        return <Level1ArcIcon className={arcIconClass} />;
      case 0:
        return <Level0ArcIcon className={arcIconClass} />;
      default:
        return <UnknownArcIcon className={arcIconClass} />;
    }
  };

  const liquidityDescription = (score?: number) => {
    switch (score) {
      case 10:
      case 9:
      case 8:
        return 'Extreme Liquidity';
      case 7:
        return 'Very Liquid';
      case 6:
        return 'Liquid';
      case 5:
        return 'Moderate Liquidity';
      case 4:
        return 'Lower Liquidity';
      case 3:
        return 'Niche Liquidity';
      case 2:
        return 'Low Liquidity';
      case 1:
        return 'Minimal Liquidity';
      case 0:
        return 'No Liquidity';
      default:
        return 'Unknown Liquidity';
    }
  };

  const parseReserveUsd = (reserveInUsd?: string | null): number | null => {
    if (reserveInUsd === null || reserveInUsd === undefined || reserveInUsd === '') return null;
    try {
      return new Decimal(reserveInUsd).toNumber();
    } catch {
      return null;
    }
  };

  const hasReserveUsd = (
    pool: TopPool & { reserveInUsd: number | null },
  ): pool is TopPool & { reserveInUsd: number } => pool.reserveInUsd !== null;

  const sortedPools =
    topPools?.data
      .map((pool) => ({
        ...pool,
        reserveInUsd: parseReserveUsd(pool.attributes.reserve_in_usd),
      }))
      .filter(hasReserveUsd)
      .sort((a, b) => b.reserveInUsd - a.reserveInUsd)
      .slice(0, 5) ?? [];

  const getContainerClass = (level: LIQUIDITY_LEVEL) => {
    const baseClass =
      'text-sm font-medium flex flex-col items-center justify-center rounded-[16px] px-6 relative overflow-hidden border border-solid border-token-gray-200';

    switch (level) {
      case LIQUIDITY_LEVEL.HIGH:
        return fontStyles({
          variant: 'LABEL_SM',
          class: [`${baseClass} bg-token-green-100`],
        });
      case LIQUIDITY_LEVEL.MEDIUM:
        return fontStyles({
          variant: 'LABEL_SM',
          class: [`${baseClass} bg-token-orange-200`],
        });
      case LIQUIDITY_LEVEL.LOW:
        return fontStyles({
          variant: 'LABEL_SM',
          class: [`${baseClass} bg-token-red-200`],
        });
      case LIQUIDITY_LEVEL.UNKNOWN:
      default:
        return fontStyles({
          variant: 'LABEL_SM',
          class: [`${baseClass} bg-token-alphalight-100`],
        });
    }
  };

  const getTextClass = (level: LIQUIDITY_LEVEL) => {
    const baseClasses = 'text-500 font-semibold  flex justify-center -mt-5 mb-4';

    switch (level) {
      case LIQUIDITY_LEVEL.HIGH:
        return `${baseClasses} text-token-green-900 `;
      case LIQUIDITY_LEVEL.MEDIUM:
        return `${baseClasses} text-token-orange-900`;
      case LIQUIDITY_LEVEL.LOW:
        return `${baseClasses} text-token-red-900`;
      case LIQUIDITY_LEVEL.UNKNOWN:
      default:
        return `${baseClasses} text-token-gray-800`;
    }
  };

  const getDescriptionClass = (level: LIQUIDITY_LEVEL) => {
    const baseClasses = 'font-medium text-label-xsm flex justify-center items-center';

    switch (level) {
      case LIQUIDITY_LEVEL.HIGH:
        return `${baseClasses} text-token-green-900`;
      case LIQUIDITY_LEVEL.MEDIUM:
        return `${baseClasses} text-token-orange-900`;
      case LIQUIDITY_LEVEL.LOW:
        return `${baseClasses} text-token-red-900`;
      case LIQUIDITY_LEVEL.UNKNOWN:
      default:
        return `${baseClasses} text-token-gray-900`;
    }
  };

  const getHighlightClass = (level: LIQUIDITY_LEVEL) => {
    const baseClasses =
      'absolute h-full w-full top-[-25%] left-0 right-auto pointer-events-none overflow-hidden rotate-[-16.841deg]';

    switch (level) {
      case LIQUIDITY_LEVEL.HIGH:
        return `${baseClasses} bg-[radial-gradient(50%_50%_at_50%_50%,#35D600_0%,rgba(78,225,54,0.00)_100%)] opacity-15`;
      case LIQUIDITY_LEVEL.MEDIUM:
        return `${baseClasses} bg-[radial-gradient(57.79%_58.91%_at_51.75%_41.12%,#FF9A3B_0%,rgba(225,197,54,0.00)_100%)] opacity-15`;
      case LIQUIDITY_LEVEL.LOW:
        return `${baseClasses} bg-[radial-gradient(50%_50%_at_50%_50%,#FF004D_0%,rgba(255,127,127,0.00)_100%)] opacity-10`;
      case LIQUIDITY_LEVEL.UNKNOWN:
      default:
        return '';
    }
  };

  return (
    <div className="flex flex-col scroll-mt-[62px]" id="liquidityScore">
      <h2 className={fontStyles({ variant: 'H5', class: ['flex', ' px-6', ' pt-6'] })}>
        Liquidity Score
      </h2>
      <div className="grid gap-6 p-6 grid-cols-1 tablet:grid-cols-2 h-full auto-rows-fr">
        <div className={getContainerClass(currentLiquidityLevel)}>
          {currentLiquidityLevel !== LIQUIDITY_LEVEL.UNKNOWN && (
            <div className={getHighlightClass(currentLiquidityLevel)} />
          )}
          {LiquidityArcIcon(liquidityScore)}
          {currentLiquidityLevel !== LIQUIDITY_LEVEL.UNKNOWN ? (
            <p className={getTextClass(currentLiquidityLevel)}>{liquidityScore}/10</p>
          ) : (
            <UnknownBadgeIcon className="-mt-8 mb-4" />
          )}
          <p className={getDescriptionClass(currentLiquidityLevel)}>
            {currentLiquidityLevel !== LIQUIDITY_LEVEL.UNKNOWN && (
              <span className="pr-1">{icon}</span>
            )}
            {liquidityDescription(liquidityScore)}
          </p>
        </div>

        {sortedPools.length === 0 || liquidityScore == null ? (
          <div className="p-4 rounded-[16px] border border-solid border-[rgba(0,0,0,0.02)] bg-token-alphalight-100 h-48">
            <div className="flex justify-between">
              <div className={fontStyles({ variant: 'LABEL_XS', class: ['text-token-gray-800'] })}>
                Pool
              </div>
              <div className={fontStyles({ variant: 'LABEL_XS', class: ['text-token-gray-800'] })}>
                Pair
              </div>
              <div
                className={fontStyles({
                  variant: 'LABEL_XS',
                  class: ['text-token-gray-800 text-right'],
                })}
              >
                Liquidity
              </div>
            </div>
            <div
              className={fontStyles({
                variant: 'BODY_SM',
                class: ['pt-16 text-center text-token-gray-700'],
              })}
            >
              No data available
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-[3fr_2fr_1fr] gap-x-4 gap-y-1 p-4 rounded-[16px] border border-solid border-token-gray-200 bg-token-alphalight-100 h-48">
            <div className={fontStyles({ variant: 'LABEL_XS', class: ['text-token-gray-800'] })}>
              Pool
            </div>
            <div
              className={fontStyles({ variant: 'LABEL_XS', class: ['mb-4 text-token-gray-800'] })}
            >
              Pair
            </div>
            <div
              className={fontStyles({
                variant: 'LABEL_XS',
                class: ['mb-4 text-token-gray-800 text-right'],
              })}
            >
              Liquidity
            </div>
            {sortedPools.map((pool) => (
              <React.Fragment key={pool.id}>
                <div
                  className={fontStyles({
                    variant: 'BODY_SM',
                    class: ['flex min-w-0 gap-1 items-center'],
                  })}
                >
                  <LiquidityProviderAvatar
                    name={pool.relationships.dex.data.id}
                    alt="Liquidity source icon"
                    size={16}
                  />
                  <div className="overflow-hidden whitespace-nowrap overflow-ellipsis text-token-gray-1600">
                    {getLiquiditySourceLabel(pool.relationships.dex.data.id)}
                  </div>
                </div>
                <div
                  key={`pair-${pool.id}`}
                  className={fontStyles({
                    variant: 'BODY_SM',
                    class: ['flex min-w-0 items-center'],
                  })}
                >
                  <div className="overflow-hidden whitespace-nowrap overflow-ellipsis text-token-gray-900">
                    {pool.attributes.name.replace(/\s+/g, '').replace(/[0-9.%]/g, '')}
                  </div>
                </div>
                <div
                  key={`liquidity-${pool.id}`}
                  className={fontStyles({
                    variant: 'BODY_SM',
                    class: ['flex justify-end text-token-gray-900 items-center'],
                  })}
                >{`${currencySymbol}${abbreviateNumber(convertFromUsd(pool.reserveInUsd), 2)}`}</div>
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      <button
        className={fontStyles({
          variant: 'LABEL_MD',
          class: [
            'cursor-pointer text-center text-token-gray w-full h-[44px] border-t-token-gray-200 text-token-gray-800 border-t-[1px] rounded-b-2xl hover:bg-token-alphalight-100',
          ],
        })}
        onClick={() => {
          setIsOpen(true);
          ampli.liquidityScoreLearnMoreClicked({
            token_chain_id: activeToken.chainId,
            token_chain_name: activeToken.chainName,
            token_address: activeToken.address,
            token_symbol: activeToken.symbol,
          });
        }}
      >
        Learn more
      </button>
      <LiquidityScoreModal
        activeToken={activeToken}
        currentScore={liquidityScore}
        isOpen={isOpen}
        onOpenChange={setIsOpen}
      />
    </div>
  );
};
